import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { decode } from 'he';
import { Link } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import {
  GET_PUBLIC_SITE,
  GET_TINYCMS_ARTICLES,
  GET_TINYCMS_SITE,
  UPDATE_PUBLIC_SITE,
  UPDATE_TINYCMS_SITE,
  CREATE_TINYCMS_PREVIEW,
  PUBLISH_TINYCMS_CONTENT,
  TINYCMS_SITE_CHANGES,
  DISCARD_TINYCMS_SITE_CHANGES,
} from '../../graphql';
import { AnalyticsSettings } from '../settings/Analytics';
import { APPLY_TINYCMS_STARTER, TINYCMS_STARTERS, useTemplateFonts, type Starter } from '../account/StarterStep';
import { fontStack } from '../account/StarterThumb';
import { StarterThumb } from '../account/StarterThumb';
import { SitePreviewFrame, previewAddresses } from './SitePreviewFrame';
import { DomainsSettings } from '../settings/Domains';
import { FeedKeysSettings } from '../settings/FeedKeys';
import { RootFilesSettings } from '../settings/RootFiles';
import { ApprovalHistory, ChangeList, Finding, InkSection, type SiteChange } from './shared';
import { Loader } from '../common/PageLoader';

/** The live version's look, or null before the first publish. */
const TINYCMS_LIVE_LOOK = gql`
  query TinycmsLiveLook($accountId: ID!) {
    tinycmsLiveLook(accountId: $accountId)
  }
`;

/** Back to the live look: the way out after trying a template. */
const REVERT_TINYCMS_SITE_THEME = gql`
  mutation RevertTinycmsSiteTheme($accountId: ID!) {
    revertTinycmsSiteTheme(accountId: $accountId)
  }
`;

interface LiveLook {
  version: number;
  starter: { id: string } | null;
}

/**
 * The site, in two places ("TinyCMS UX optimization review v6", Prototype v6):
 *   design    Content → Design: the theme, what agents may change, history
 *   settings  Site settings: visibility, identity, domain, analytics & code,
 *             verification & ads.txt, licensed feeds, and links to each content type's rules
 * Visibility takes effect immediately. Theme edits (and the logo, cover and
 * icon) stay on the working copy until Publish. Domain and analytics keep their
 * own saves.
 */

const COLOR_KEYS = ['background', 'text', 'accent', 'muted'] as const;
const COLOR_LABELS: Record<(typeof COLOR_KEYS)[number], string> = {
  background: 'Background',
  text: 'Text',
  accent: 'Links & accent',
  muted: 'Secondary text',
};
const HEX = /^#[0-9a-f]{6}$/i;
const SURFACES: Array<{ key: string; name: string; desc: string; def: number }> = [
  { key: 'home', name: 'Homepage layout', desc: 'Modules, order, featured stories', def: 1 },
  { key: 'hubs', name: 'Topic hubs', desc: 'Landing pages for each topic', def: 0 },
  { key: 'nav', name: 'Navigation', desc: 'Header and footer links', def: 1 },
  { key: 'theme', name: 'Theme', desc: 'Type, color, logo', def: 2 },
  { key: 'public', name: 'Public fields', desc: 'Which custom fields readers can see', def: 2 },
];
const PERM_LABELS = [
  { label: 'Agents can edit', bg: 'var(--ink-green-bg)', color: 'var(--ink-green)' },
  { label: 'Ask first', bg: 'var(--ink-amber-bg)', color: 'var(--ink-amber)' },
  { label: 'Locked', bg: 'var(--ink-bg3)', color: 'var(--ink-ink2)' },
];

interface SiteForm {
  preset: string;
  colors: Record<string, string>;
  fonts: { heading: string; body: string };
  layout: { home: string; width: string; showAsk: boolean; corners: string; photos: string };
  logoUrl: string;
  logoAlt: string;
  coverUrl: string;
  coverAlt: string;
  iconUrl: string;
}

const toForm = (resolved: any): SiteForm => ({
  preset: resolved?.preset || 'classic',
  colors: { ...(resolved?.colors || {}) },
  // A Google Fonts choice is stored as { google, category, weights }; the form
  // holds its family name, so an untouched font is never re-sent.
  fonts: {
    heading: fontName(resolved?.fonts?.heading) || 'serif',
    body: fontName(resolved?.fonts?.body) || 'sans',
  },
  layout: {
    home: resolved?.layout?.home || 'feed',
    width: resolved?.layout?.width || 'wide',
    showAsk: resolved?.layout?.showAsk !== false,
    corners: resolved?.layout?.corners || 'round',
    photos: resolved?.layout?.photos || 'colour',
  },
  logoUrl: resolved?.imagery?.logo?.url || '',
  logoAlt: resolved?.imagery?.logo?.alt || '',
  coverUrl: resolved?.imagery?.cover?.url || '',
  coverAlt: resolved?.imagery?.cover?.alt || '',
  iconUrl: resolved?.imagery?.icon?.url || '',
});

function fontName(v: unknown): string | null {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object' && typeof (v as { google?: unknown }).google === 'string') {
    return (v as { google: string }).google;
  }
  return null;
}

function diff(a: SiteForm, b: SiteForm) {
  const patch: Record<string, any> = {};
  if (a.preset !== b.preset) patch.preset = b.preset;
  const colors: Record<string, string> = {};
  for (const k of COLOR_KEYS)
    if ((a.colors[k] || '') !== (b.colors[k] || '')) colors[k] = b.colors[k];
  if (Object.keys(colors).length) patch.colors = colors;
  const fonts: Record<string, string> = {};
  if (a.fonts.heading !== b.fonts.heading) fonts.heading = b.fonts.heading;
  if (a.fonts.body !== b.fonts.body) fonts.body = b.fonts.body;
  if (Object.keys(fonts).length) patch.fonts = fonts;
  const layout: Record<string, any> = {};
  if (a.layout.home !== b.layout.home) layout.home = b.layout.home;
  if (a.layout.width !== b.layout.width) layout.width = b.layout.width;
  if (a.layout.showAsk !== b.layout.showAsk) layout.showAsk = b.layout.showAsk;
  if (a.layout.corners !== b.layout.corners) layout.corners = b.layout.corners;
  if (a.layout.photos !== b.layout.photos) layout.photos = b.layout.photos;
  if (Object.keys(layout).length) patch.layout = layout;
  const imagery: Record<string, any> = {};
  if (a.logoUrl !== b.logoUrl || a.logoAlt !== b.logoAlt) {
    imagery.logo = b.logoUrl ? { url: b.logoUrl, alt: b.logoAlt } : null;
  }
  if (a.coverUrl !== b.coverUrl || a.coverAlt !== b.coverAlt) {
    imagery.cover = b.coverUrl ? { url: b.coverUrl, alt: b.coverAlt } : null;
  }
  if (a.iconUrl !== b.iconUrl) imagery.icon = b.iconUrl ? { url: b.iconUrl } : null;
  if (Object.keys(imagery).length) patch.imagery = imagery;
  return patch;
}

const labelOf = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

type Vis = 'public' | 'password' | 'offline';

function visOf(
  account: { publicEnabled?: boolean; publicAccessProtected?: boolean } | null | undefined
): Vis {
  if (!account?.publicEnabled) return 'offline';
  if (account.publicAccessProtected) return 'password';
  return 'public';
}

export const CmsSite: React.FC<{ mode?: 'design' | 'settings' }> = ({ mode = 'design' }) => {
  const design = mode === 'design';
  const settings = mode === 'settings';
  const { accountId } = useAccount();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_SITE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: publicData, refetch: refetchPublic } = useQuery(GET_PUBLIC_SITE, {
    variables: { accountId },
    skip: !accountId,
  });
  const { data: articlesData } = useQuery(GET_TINYCMS_ARTICLES, {
    variables: { accountId, state: 'published' },
    skip: !accountId,
  });
  useEffect(() => {
    if (loading) return;
    const id = window.location.hash.replace('#', '');
    if (!id) return;
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [loading]);
  const [updateSite, { loading: saving }] = useMutation(UPDATE_TINYCMS_SITE);
  // Templates (UX review v10): switching keeps every page and article.
  const { data: startersData } = useQuery<{ tinycmsStarters: Starter[] }>(TINYCMS_STARTERS, { skip: !design });
  const [applyStarter, { loading: applyingTemplate }] = useMutation(APPLY_TINYCMS_STARTER);
  const { data: liveLookData, refetch: refetchLiveLook } = useQuery<{ tinycmsLiveLook: LiveLook | null }>(
    TINYCMS_LIVE_LOOK,
    { variables: { accountId }, skip: !design || !accountId }
  );
  const [revertTheme, { loading: reverting }] = useMutation(REVERT_TINYCMS_SITE_THEME);
  // "What changed": the saved working copy against the live version, loaded
  // when the bar's toggle opens and again whenever the working copy changes.
  const [showChanges, setShowChanges] = useState(false);
  const {
    data: changesData,
    loading: changesLoading,
    refetch: refetchChanges,
  } = useQuery<{ tinycmsSiteChanges: { changes: SiteChange[] } }>(TINYCMS_SITE_CHANGES, {
    variables: { accountId },
    skip: !design || !accountId || !showChanges,
    // Always fresh: a stale list from before a save or publish would mislead.
    fetchPolicy: 'network-only',
  });
  const [discardChanges, { loading: discarding }] = useMutation(DISCARD_TINYCMS_SITE_CHANGES);
  useTemplateFonts();
  const [updatePublic] = useMutation(UPDATE_PUBLIC_SITE);
  const [publishContent, { loading: publishing }] = useMutation(PUBLISH_TINYCMS_CONTENT);
  const [createPreview, { loading: previewing }] = useMutation(CREATE_TINYCMS_PREVIEW);

  const site = data?.tinycmsSite;
  const account = publicData?.account;
  const loaded = useMemo(() => toForm(site?.resolved), [site]);
  const [form, setForm] = useState<SiteForm>(loaded);
  const [title, setTitle] = useState('');
  const [tagline, setTagline] = useState('');
  const [password, setPassword] = useState('');
  const [askPassword, setAskPassword] = useState(false);
  const [override, setOverride] = useState('');
  const [perms, setPerms] = useState<Record<string, number>>({});
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => setForm(loaded), [loaded]);
  // The site's name and tagline live in the site document (they publish with
  // the theme, like everything readers see); the account's Public Site values
  // are the fallback until one is set.
  const shownTitle = site?.working?.identity?.title ?? account?.publicTitle ?? '';
  const shownTagline = site?.working?.identity?.tagline ?? account?.publicTagline ?? '';
  useEffect(() => {
    setTitle(shownTitle);
    setTagline(shownTagline);
  }, [shownTitle, shownTagline]);

  const patch = diff(loaded, form);
  const dirty = Object.keys(patch).length > 0;
  const unpublished = Boolean(site?.state?.hasUnpublishedChanges);
  const liveVersion = site?.state?.live?.version || 0;
  const workingHash = site?.workingHash;
  useEffect(() => {
    if (showChanges && accountId) void refetchChanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingHash]);
  useEffect(() => {
    if (!unpublished) setShowChanges(false);
  }, [unpublished]);
  const findings: Finding[] = site?.state?.findings || [];
  const blocking = findings.filter((f) => f.severity === 'blocking').length;

  const savedVis = visOf(account);
  const vis: Vis = askPassword ? 'password' : savedVis;
  // The server knows the real address (custom domain, platform host or free address).
  const host = site?.siteUrl ? String(site.siteUrl).replace(/^https?:\/\//, '') : null;

  // The real site in the Design header, not a drawing of the tokens: one
  // preview token per visit gives both addresses — the working copy
  // (?preview=) and the live site at the same origin.
  const [frames, setFrames] = useState<{ working: string; live: string } | null>(null);
  useEffect(() => {
    if (!design || !host || !accountId || frames) return;
    let gone = false;
    createPreview({ variables: { accountId } })
      .then(({ data: made }) => {
        const url = made?.createTinycmsPreview?.url;
        if (!gone && url) setFrames(previewAddresses(url));
      })
      .catch(() => {
        /* no handle yet, or offline: the header falls back to the drawn theme */
      });
    return () => {
      gone = true;
    };
  }, [design, host, accountId, frames, createPreview]);
  const siteTitle = title.trim() || account?.name || 'Site';
  const titles: string[] = (articlesData?.tinycmsArticles || [])
    .map((a: { title?: string }) => decode(a.title || ''))
    .filter(Boolean)
    .slice(0, 3);

  const saveWorking = async () => {
    if (!dirty) return site?.workingHash as string;
    for (const key of COLOR_KEYS) {
      if (form.colors[key] && !HEX.test(form.colors[key])) {
        throw new Error(`${COLOR_LABELS[key]} needs a color like #1a2b3c.`);
      }
    }
    await updateSite({ variables: { accountId, input: patch } });
    const fresh = await refetch();
    return fresh.data?.tinycmsSite?.workingHash as string;
  };

  const publishTheme = async () => {
    setMessage(null);
    try {
      const hash = await saveWorking();
      const { data: published } = await publishContent({
        variables: {
          accountId,
          kind: 'site',
          action: 'publish',
          contentHash: hash,
          overrideReason: override.trim() || undefined,
        },
      });
      const r = published?.publishTinycmsContent;
      if (r?.ok) {
        setOverride('');
        // The live look is what "Your own look" shows; it just changed.
        void refetchLiveLook();
        setMessage({
          tone: 'ok',
          text: r.unchanged ? 'Already live.' : `Published theme v${r.version}.`,
        });
      } else {
        setMessage({ tone: 'error', text: r?.message || 'Publish was refused.' });
      }
      await refetch();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  /** Everything changed since the live version goes; readers see nothing different. */
  const discardWorking = async () => {
    const question = liveVersion
      ? `Readers keep seeing theme v${liveVersion}. Everything changed since then goes.`
      : 'Readers keep seeing the default theme. Everything changed since then goes.';
    if (!window.confirm(question)) return;
    setMessage(null);
    try {
      await discardChanges({ variables: { accountId } });
      // The refetched site recomputes `loaded`, and the effect on it resets the form.
      await Promise.all([refetch(), refetchLiveLook()]);
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  /** A published version back on the working copy (History → Restore this version). */
  const afterRestore = async () => {
    setMessage(null);
    await Promise.all([refetch(), refetchLiveLook()]);
  };

  const preview = async () => {
    setMessage(null);
    const win = window.open('', '_blank');
    try {
      await saveWorking();
      const { data: created } = await createPreview({ variables: { accountId } });
      const url = created?.createTinycmsPreview?.url;
      if (win && url) win.location.href = url;
      else win?.close();
    } catch (err: any) {
      win?.close();
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const savePublic = async (input: Record<string, unknown>) => {
    setMessage(null);
    try {
      await updatePublic({ variables: { accountId, input } });
      await refetchPublic();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const chooseVis = async (next: Vis) => {
    if (next === 'password') {
      setAskPassword(true);
      return;
    }
    setAskPassword(false);
    if (next === 'offline') {
      await savePublic({ publicEnabled: false });
      return;
    }
    if (account?.publicAccessProtected) {
      if (
        !window.confirm(
          'Remove the password? Anyone with the address will be able to see this site.'
        )
      )
        return;
      await savePublic({ publicEnabled: true, publicAccessPassword: '' });
      return;
    }
    await savePublic({ publicEnabled: true });
  };

  const savePassword = async () => {
    if (!password.trim()) {
      setMessage({ tone: 'error', text: 'Enter a password to protect the site.' });
      return;
    }
    await savePublic({ publicEnabled: true, publicAccessPassword: password });
    setPassword('');
    setAskPassword(false);
  };

  const saveIdentity = async () => {
    const nextTitle = title.trim();
    const nextTagline = tagline.trim();
    if (nextTitle === shownTitle && nextTagline === shownTagline) return;
    setMessage(null);
    try {
      // Onto the working copy: readers see it when the theme is next published.
      await updateSite({
        variables: {
          accountId,
          input: { identity: { title: nextTitle || null, tagline: nextTagline || null } },
        },
      });
      await refetch();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const currentTemplate: string | null = site?.working?.starter?.id || null;

  /** Switch the working theme to a template's tokens; pages and articles stay. */
  const chooseTemplate = async (id: string) => {
    setMessage(null);
    try {
      await applyStarter({ variables: { accountId, starterId: id, only: 'look' } });
      await refetch();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  /**
   * The template's look AND its pages: its sample pages, forms, navigation
   * and home page as unpublished working copies, in the site's name. Pages
   * that already exist at an address are kept as they are.
   */
  const chooseTemplatePages = async (t: Starter) => {
    setMessage(null);
    const ok = window.confirm(
      `Add the ${t.name} template’s pages to ${siteTitle}?\n\nIts sample pages, navigation and home page are added as unpublished drafts with placeholder words for you or your agent to rewrite. Pages you already have are kept. Nothing is published.`
    );
    if (!ok) return;
    try {
      const r = await applyStarter({ variables: { accountId, starterId: t.id, only: 'all' } });
      const result = r.data?.applyTinycmsStarter || {};
      const pages: Array<{ kept?: boolean; title?: string }> = result.pages || [];
      const added = pages.filter((p) => !p.kept).length;
      const kept = pages.length - added;
      await refetch();
      setMessage({
        tone: 'ok',
        text:
          `${t.name} look applied. ${added} page${added === 1 ? '' : 's'} added as drafts` +
          (kept ? `, ${kept} you already had kept` : '') +
          '. Rewrite them under Pages, then publish when ready.',
      });
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  /** Back to the live look (and its template record, if it had one). */
  const revertLook = async () => {
    setMessage(null);
    try {
      await revertTheme({ variables: { accountId } });
      await Promise.all([refetch(), refetchLiveLook()]);
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const liveLook = liveLookData?.tinycmsLiveLook ?? null;
  // Custom CSS (set by the agent over MCP) sits on top of whatever template is
  // chosen: a template replaces the tokens underneath it, not the CSS.
  const customCss: string = typeof site?.working?.customCss === 'string' ? site.working.customCss : '';
  const customCssLines = customCss.trim() ? customCss.trim().split('\n').length : 0;
  // "Your own look" is offered while the live site is not a template: the
  // way back for a site styled by hand (every site from before templates).
  const ownLookOffered = !liveLook?.starter;
  const ownLookCurrent = !currentTemplate;

  const resetPreset = async () => {
    setMessage(null);
    try {
      if (currentTemplate) {
        await applyStarter({ variables: { accountId, starterId: currentTemplate, only: 'look' } });
      } else {
        await updateSite({
          variables: {
            accountId,
            input: {
              colors: Object.fromEntries(COLOR_KEYS.map((k) => [k, null])),
              fonts: { heading: null, body: null },
              layout: { home: null, width: null, showAsk: null, corners: null, photos: null },
            },
          },
        });
      }
      await refetch();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  if (loading && !site) {
    return (
      <div className="ink-body">
        <Loader style={{ color: 'var(--ink-ink3)' }} />
      </div>
    );
  }
  if (!site) {
    return (
      <div className="ink-body">
        <p style={{ color: 'var(--ink-ink3)' }}>The site settings are unavailable.</p>
      </div>
    );
  }

  const visMeta: Record<Vis, { label: string; dot: string }> = {
    public: { label: 'Live · Public', dot: 'var(--ink-green)' },
    password: { label: 'Live · Password protected', dot: 'var(--ink-amber)' },
    offline: { label: 'Offline', dot: 'var(--ink-ink3)' },
  };
  const showBar = dirty || unpublished;
  const nextVersion = liveVersion + 1;
  const list = form.layout.home === 'list';
  const maxw =
    form.layout.width === 'narrow' ? '62%' : form.layout.width === 'standard' ? '80%' : '100%';
  const previewItems = titles.length
    ? titles
    : ['Your latest article', 'Another story', 'One more'];

  return (
    <div className={design ? 'ink-site-embedded' : 'ink-body'}>
      {showBar ? (
        <div className="ink-site-bar">
          <span
            className="ink-dot"
            style={{ width: 8, height: 8, background: 'var(--ink-amber)' }}
          />
          <span style={{ font: '500 14px var(--ink-font)' }}>
            Theme changes are on the working copy.
          </span>
          <span className="ink-site-bar-note">
            {liveVersion
              ? `Readers still see theme v${liveVersion}.`
              : 'Readers still see the default theme.'}
          </span>
          <span className="ink-site-bar-actions">
            {unpublished ? (
              <button
                type="button"
                className="ink-btn ink-btn-quiet"
                onClick={() => setShowChanges((v) => !v)}
                aria-expanded={showChanges}
              >
                {showChanges ? 'Hide changes' : 'What changed'}
              </button>
            ) : null}
            {unpublished ? (
              <button
                type="button"
                className="ink-btn ink-btn-quiet"
                onClick={() => void discardWorking()}
                disabled={discarding || saving || publishing}
              >
                {discarding ? 'Discarding…' : 'Discard changes'}
              </button>
            ) : dirty ? (
              <button
                type="button"
                className="ink-btn ink-btn-quiet"
                onClick={() => setForm(loaded)}
              >
                Discard
              </button>
            ) : null}
            <button
              type="button"
              className="ink-btn ink-btn-quiet"
              onClick={() => void preview()}
              disabled={previewing || saving}
            >
              Preview ↗
            </button>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => void publishTheme()}
              disabled={publishing || saving || (blocking > 0 && !override.trim())}
            >
              {publishing || saving ? 'Publishing…' : `Publish theme v${nextVersion}`}
            </button>
          </span>
          {blocking > 0 ? (
            <input
              className="ink-site-input"
              style={{ flex: '1 1 220px' }}
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              placeholder="Override reason (recorded with this publish)"
            />
          ) : null}
          {showChanges && unpublished ? (
            <div className="ink-site-bar-changes">
              {changesLoading ? (
                <p className="ink-changes-empty">Comparing…</p>
              ) : (
                <ChangeList
                  changes={changesData?.tinycmsSiteChanges?.changes || []}
                  empty="The saved working copy is the same as what readers see."
                />
              )}
              {dirty ? (
                <p className="ink-changes-empty">
                  Edits you haven’t saved yet aren’t listed; Preview or Publish saves them.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {settings ? (
        <div className="ink-site-settings-head">
          <Link to="/content?view=design" className="ink-back-link">
            ← Content
          </Link>
          <h1 className="ink-h1">Site settings</h1>
          <p className="ink-lede">
            Who can see {siteTitle}, its address and what runs on it. How it looks lives in{' '}
            <Link to="/content?view=design">Content → Design</Link>.
          </p>
        </div>
      ) : null}

      {design ? (
        <div className="ink-site-hero">
          <div>
            <div className="ink-site-status">
              <span
                className="ink-dot"
                style={{ width: 8, height: 8, background: visMeta[vis].dot }}
              />
              {visMeta[vis].label}
            </div>
            <h1 className="ink-site-name">{siteTitle}</h1>
            {host ? (
              <a className="ink-site-url" href={`https://${host}`} target="_blank" rel="noreferrer">
                {host} ↗
              </a>
            ) : (
              <div className="ink-site-url" style={{ color: 'var(--ink-ink3)' }}>
                Choose a handle to give the site an address.
              </div>
            )}
          </div>
          {frames ? (
            <SitePreviewFrame
              src={unpublished ? frames.working : frames.live}
              height={320}
              title={unpublished ? 'The working copy of your site' : 'Your site as readers see it'}
              reloadKey={`${site?.workingHash || ''}:${unpublished ? 'working' : 'live'}`}
            >
              <span className="ink-site-badge">{dirty || unpublished ? 'Working copy' : 'Live'}</span>
              <a
                className="ink-site-frame-open"
                href={unpublished ? frames.working : frames.live}
                target="_blank"
                rel="noreferrer"
              >
                Open ↗
              </a>
            </SitePreviewFrame>
          ) : (
          <div
            className="ink-site-preview"
            style={{
              background: form.colors.background || '#fff',
              color: form.colors.text || '#111',
            }}
          >
            <span className="ink-site-badge">
              {dirty || unpublished ? 'Working theme' : 'Live theme'}
            </span>
            <div className="ink-site-preview-head">
              <span style={{ font: `700 17px/1.2 ${fontStack(form.fonts.heading, 'serif')}` }}>
                {siteTitle}
              </span>
              {tagline ? (
                <span
                  style={{
                    font: `400 12px/1.3 ${fontStack(form.fonts.body, 'sans')}`,
                    color: form.colors.muted,
                  }}
                >
                  {tagline}
                </span>
              ) : null}
            </div>
            <div className="ink-site-preview-body" style={{ maxWidth: maxw }}>
              {list ? (
                previewItems.map((item) => (
                  <div
                    key={item}
                    style={{
                      padding: '8px 0',
                      borderBottom: '1px solid color-mix(in srgb, currentColor 14%, transparent)',
                    }}
                  >
                    <div
                      style={{ font: `600 14px/1.3 ${fontStack(form.fonts.heading, 'serif')}` }}
                    >
                      {item}
                    </div>
                    <div
                      style={{
                        font: `400 11.5px/1.3 ${fontStack(form.fonts.body, 'sans')}`,
                        color: form.colors.muted,
                        marginTop: 2,
                      }}
                    >
                      <span style={{ color: form.colors.accent }}>Read →</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="ink-site-cards">
                  {previewItems.map((item) => (
                    <div key={item}>
                      <div className="ink-site-card-thumb" />
                      <div
                        style={{
                          font: `600 12px/1.3 ${fontStack(form.fonts.heading, 'serif')}`,
                          marginTop: 6,
                        }}
                      >
                        {item}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      ) : null}

      {message ? (
        <p
          style={{
            margin: '16px 0 0',
            font: '400 14px var(--ink-font)',
            color: message.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-green)',
          }}
        >
          {message.text}
        </p>
      ) : null}

      {settings ? (
        <InkSection
          id="visibility"
          title="Visibility"
          description="Changes take effect immediately. Nothing stays cached."
        >
          <div className="ink-site-choices">
            {(
              [
                ['public', 'Public', 'Anyone with the link, and search engines.'],
                ['password', 'Password', 'Private while you build it out.'],
                ['offline', 'Offline', 'Content stays here. The site is down.'],
              ] as const
            ).map(([key, label, desc]) => (
              <button
                key={key}
                type="button"
                className="ink-site-choice"
                data-on={vis === key}
                onClick={() => void chooseVis(key)}
              >
                <span className="ink-site-choice-title">
                  {label}
                  <span className="ink-site-radio" />
                </span>
                <span className="ink-site-choice-desc">{desc}</span>
              </button>
            ))}
          </div>
          {vis === 'password' ? (
            <div className="ink-site-pass">
              <input
                className="ink-site-input"
                type="password"
                value={password}
                autoComplete="new-password"
                placeholder={
                  account?.publicAccessProtected
                    ? 'Leave blank to keep the current password'
                    : 'Set a password'
                }
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                onClick={() => void savePassword()}
              >
                Save password
              </button>
            </div>
          ) : null}
        </InkSection>
      ) : null}

      {design ? (
        <InkSection
          id="theme"
          title="Template"
          description="The look of your public site. Each picture is the template’s own front page in your words and with your cover photo. Use the look keeps every page and article and changes colours, type, shapes and the header; Look and pages also adds the template’s pages as drafts. Changes stay on the working theme until you publish."
        >
          <div className="ink-site-stack">
            {customCssLines ? (
              <p className="ink-tpl-css-note" role="note">
                <strong>This site has its own CSS on top of the theme.</strong>{' '}
                {customCssLines === 1 ? 'One line' : `${customCssLines} lines`} of custom CSS, set by your
                agent, restyle parts of the site. A template changes the colours, fonts and shapes underneath
                that CSS, so some of the template may not show. Ask your agent to review or clear the site’s
                custom CSS before judging a template.
              </p>
            ) : null}
            <div className="ink-tpl-grid">
              {ownLookOffered ? (
                <button
                  type="button"
                  className="ink-tpl-card ink-tpl-own"
                  data-on={ownLookCurrent}
                  disabled={applyingTemplate || reverting || ownLookCurrent}
                  onClick={() => void revertLook()}
                >
                  <span className="ink-tpl-thumb">
                    {frames && liveLook ? (
                      <SitePreviewFrame src={frames.live} height={190} title="Your site as readers see it" />
                    ) : (
                      <OwnLookThumb form={form} siteTitle={siteTitle} tagline={tagline} />
                    )}
                    {ownLookCurrent ? (
                      <span className="ink-tpl-badge" data-tone={unpublished ? 'working' : 'live'}>
                        {unpublished ? 'Working copy' : 'Live'}
                      </span>
                    ) : null}
                  </span>
                  <span className="ink-tpl-text">
                    <span className="ink-tpl-name">Your own look</span>
                    <span className="ink-tpl-blurb">
                      {ownLookCurrent
                        ? 'Your colours, fonts and layout, as they are.'
                        : liveLook
                          ? 'Go back to the look readers see today.'
                          : 'Go back to the default look.'}
                    </span>
                  </span>
                </button>
              ) : null}
              {(startersData?.tinycmsStarters || []).map((t) => {
                const isCur = t.id === currentTemplate;
                return (
                  <div key={t.id} className="ink-tpl-card ink-tpl-card-static" data-on={isCur}>
                    <span className="ink-tpl-thumb">
                      <span style={{ zoom: 0.72, height: 190, display: 'block', pointerEvents: 'none' }}>
                        <StarterThumb
                          starter={t}
                          siteName={siteTitle}
                          siteAddr={host?.split('.')[0] || 'yourname'}
                          tagline={shownTagline}
                          titles={titles}
                          coverUrl={form.coverUrl}
                        />
                      </span>
                      {isCur ? (
                        <span className="ink-tpl-badge" data-tone={unpublished ? 'working' : 'live'}>
                          {unpublished ? 'Working copy' : 'Live'}
                        </span>
                      ) : null}
                    </span>
                    <span className="ink-tpl-text">
                      <span className="ink-tpl-name">{t.name}</span>
                      <span className="ink-tpl-blurb">{t.blurb}</span>
                      <span className="ink-tpl-note">
                        The layout in the picture comes with its pages; the look alone changes colours, type,
                        shapes and the header.
                      </span>
                      <span className="ink-tpl-actions">
                        <button
                          type="button"
                          className="ink-btn ink-btn-quiet ink-btn-sm"
                          disabled={applyingTemplate}
                          onClick={() => void chooseTemplate(t.id)}
                        >
                          Use the look
                        </button>
                        <button
                          type="button"
                          className="ink-btn ink-btn-primary ink-btn-sm"
                          disabled={applyingTemplate}
                          onClick={() => void chooseTemplatePages(t)}
                        >
                          Look and pages
                        </button>
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>

            <div>
              <div className="ink-site-kicker">Colours</div>
              <div className="ink-site-swatches">
                {COLOR_KEYS.map((key) => (
                  <label key={key} className="ink-site-swatch">
                    <input
                      type="color"
                      aria-label={COLOR_LABELS[key]}
                      value={HEX.test(form.colors[key] || '') ? form.colors[key] : '#000000'}
                      onChange={(e) =>
                        setForm({ ...form, colors: { ...form.colors, [key]: e.target.value } })
                      }
                    />
                    <span>
                      <span style={{ display: 'block', font: '500 13px var(--ink-font)' }}>
                        {COLOR_LABELS[key]}
                      </span>
                      <input
                        type="text"
                        value={form.colors[key] || ''}
                        onChange={(e) =>
                          setForm({ ...form, colors: { ...form.colors, [key]: e.target.value } })
                        }
                      />
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="ink-site-controls">
              <div className="ink-site-control">
                <span style={{ font: '500 13px var(--ink-font)' }}>Fonts</span>
                <span className="ink-tpl-fonts">
                  <span style={{ font: `400 22px ${fontStack(form.fonts.heading, 'serif')}`, color: 'var(--ink-ink)' }}>Aa</span>
                  <span>
                    {form.fonts.heading} + {form.fonts.body}
                  </span>
                </span>
              </div>
              {(
                [
                  ['corners', 'Corners', ['square', 'soft', 'round'], form.layout.corners],
                  ['photos', 'Photos', ['colour', 'grey', 'duotone'], form.layout.photos],
                ] as const
              ).map(([key, label, options, value]) => (
                <div key={key} className="ink-site-control">
                  <span style={{ font: '500 13px var(--ink-font)' }}>{label}</span>
                  <span className="ink-seg">
                    {options.map((option: string) => (
                      <button
                        key={option}
                        type="button"
                        data-active={value === option}
                        onClick={() => setForm({ ...form, layout: { ...form.layout, [key]: option } })}
                      >
                        {option === 'grey' ? 'Greyscale' : labelOf(option)}
                      </button>
                    ))}
                  </span>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="ink-site-ask"
              onClick={() =>
                setForm({ ...form, layout: { ...form.layout, showAsk: !form.layout.showAsk } })
              }
            >
              <span style={{ flex: 1 }}>
                <span style={{ display: 'block', font: '500 14.5px var(--ink-font)' }}>
                  Show “Ask the archive” on the site
                </span>
                <span className="ink-site-row-desc">
                  Readers can ask questions answered from your atomized facts. Requires Atomize.
                </span>
              </span>
              <span className="ink-switch" data-on={form.layout.showAsk} />
            </button>

            <button
              type="button"
              className="ink-site-link"
              onClick={() => void resetPreset()}
              disabled={saving}
            >
              {currentTemplate ? 'Reset to the template’s own colours, fonts and photos' : 'Reset colours, fonts and layout to the defaults'}
            </button>

            {findings.length ? (
              <div>
                {findings.map((f, i) => (
                  <div
                    key={i}
                    className="ink-site-row-desc"
                    style={{
                      color: f.severity === 'blocking' ? 'var(--ink-red)' : 'var(--ink-amber)',
                    }}
                  >
                    {f.message || f.code}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </InkSection>
      ) : null}

      {design ? (
        <InkSection
          id="surface"
          title="Surface"
          description="The parts of the site readers see, and what agents are allowed to change in each. Click a permission to change it."
        >
          <div className="ink-site-card">
            {SURFACES.map((row) => {
              const index = perms[row.key] ?? row.def;
              const perm = PERM_LABELS[index];
              return (
                <div className="ink-site-surface" key={row.key}>
                  <div>
                    <div className="ink-site-surface-title">{row.name}</div>
                    <div className="ink-site-row-desc">{row.desc}</div>
                  </div>
                  <span />
                  <button
                    type="button"
                    className="ink-site-perm"
                    style={{ background: perm.bg, color: perm.color }}
                    onClick={() =>
                      setPerms((prev) => ({
                        ...prev,
                        [row.key]: ((prev[row.key] ?? row.def) + 1) % 3,
                      }))
                    }
                  >
                    {perm.label} ▾
                  </button>
                </div>
              );
            })}
          </div>
        </InkSection>
      ) : null}

      {settings ? (
        <InkSection
          id="identity"
          title="Identity"
          description="How the site introduces itself in browsers, search results and link previews. Publishes with the theme."
        >
          <div className="ink-site-fields">
            <label className="ink-site-label">
              Site title
              <input
                className="ink-site-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => void saveIdentity()}
              />
            </label>
            <label className="ink-site-label">
              Tagline
              <input
                className="ink-site-input"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                onBlur={() => void saveIdentity()}
              />
            </label>
            <div className="ink-site-label">
              Logo
              <div className="ink-site-media">
                {form.logoUrl ? (
                  <img className="ink-site-logo" src={form.logoUrl} alt="" />
                ) : (
                  <span className="ink-site-logo" />
                )}
                <input
                  className="ink-site-input"
                  value={form.logoAlt}
                  placeholder="Alt text, e.g. site logo"
                  onChange={(e) => setForm({ ...form, logoAlt: e.target.value })}
                />
              </div>
              <input
                className="ink-site-input"
                value={form.logoUrl}
                placeholder="Logo URL"
                onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
              />
            </div>
            <div className="ink-site-label">
              Home cover image
              <div className="ink-site-media">
                {form.coverUrl ? (
                  <img className="ink-site-cover" src={form.coverUrl} alt="" />
                ) : (
                  <span className="ink-site-cover" />
                )}
                <input
                  className="ink-site-input"
                  value={form.coverAlt}
                  placeholder="Alt text (required when there is an image)"
                  onChange={(e) => setForm({ ...form, coverAlt: e.target.value })}
                />
              </div>
              <input
                className="ink-site-input"
                value={form.coverUrl}
                placeholder="Cover image URL"
                onChange={(e) => setForm({ ...form, coverUrl: e.target.value })}
              />
            </div>
            <div className="ink-site-label">
              Site icon
              <div className="ink-site-media">
                {form.iconUrl ? (
                  <img className="ink-site-icon" src={form.iconUrl} alt="" />
                ) : (
                  <span className="ink-site-icon" />
                )}
                <span className="ink-site-row-desc">
                  Shown in browser tabs and on phone home screens. A square PNG, at least 180×180.
                </span>
              </div>
              <input
                className="ink-site-input"
                value={form.iconUrl}
                placeholder="Icon URL"
                onChange={(e) => setForm({ ...form, iconUrl: e.target.value })}
              />
            </div>
          </div>
        </InkSection>
      ) : null}

      {settings ? (
        <InkSection
          id="domain"
          title="Domain"
          description="Connect your own domain with two DNS records. We set up the certificate."
        >
          <DomainsSettings embedded />
        </InkSection>
      ) : null}

      {settings ? (
        <InkSection
          id="analytics"
          title="Analytics & code"
          description="Runs on the public site only. Previews never count as visits."
        >
          <AnalyticsSettings embedded />
        </InkSection>
      ) : null}

      {settings ? (
        <InkSection
          id="verification"
          title="Verification & ads.txt"
          description="Show Google, Bing and others that the site is yours, and list who may sell ads on it. Live right away — no publishing needed."
        >
          <RootFilesSettings />
        </InkSection>
      ) : null}

      {settings ? (
        <InkSection
          id="feeds"
          title="Licensed feeds"
          description="License your content to other sites and apps. Each licensee gets their own key to your full articles; see what they take and turn a key off any time."
        >
          <FeedKeysSettings />
        </InkSection>
      ) : null}

      {settings ? (
        <InkSection
          id="content-types"
          title="Content types"
          description="Rules for each kind of content. Each one also opens from its own view in Content."
        >
          <div className="ink-site-card">
            {(
              [
                ['Articles', 'Taxonomy, custom fields, publishing rules', '/content/settings'],
                ['Pages', 'Pages, header and footer', '/content?view=pages'],
              ] as const
            ).map(([name, desc, to]) => (
              <Link key={name} to={to} className="ink-site-row ink-site-row-link">
                <span>
                  <span className="ink-site-row-title">{name}</span>
                  <span className="ink-site-row-desc" style={{ display: 'block' }}>
                    {desc}
                  </span>
                </span>
                <span className="ink-site-open">Open →</span>
              </Link>
            ))}
          </div>
        </InkSection>
      ) : null}

      {design ? (
        <InkSection title="History" description="Every published theme, with who approved it.">
          <ApprovalHistory
            kind="site"
            subjectId={accountId || undefined}
            variant="ink"
            onRestored={afterRestore}
            refreshKey={liveVersion}
          />
        </InkSection>
      ) : null}
    </div>
  );
};

/**
 * A site with no live version yet, drawn from its working tokens: the header,
 * a headline and three tiles — a sketch, since there is nothing real to frame.
 */
function OwnLookThumb({ form, siteTitle, tagline }: { form: SiteForm; siteTitle: string; tagline: string }) {
  const radius = form.layout.corners === 'square' ? 0 : form.layout.corners === 'soft' ? 4 : 999;
  const accent = form.colors.accent || '#111';
  const muted = form.colors.muted || '#777';
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        height: 190,
        padding: '14px 16px',
        background: form.colors.background || '#fff',
        color: form.colors.text || '#111',
        fontFamily: fontStack(form.fonts.body, 'sans'),
        textAlign: 'left',
      }}
    >
      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ font: `600 11px ${fontStack(form.fonts.heading, 'serif')}` }}>{siteTitle}</span>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 6, color: muted }}>
          <span>About</span>
          <span>Contact</span>
          <span style={{ padding: '2px 6px', borderRadius: radius, background: accent, color: '#fff' }}>Get in touch</span>
        </span>
      </span>
      <span style={{ font: `400 20px/1.05 ${fontStack(form.fonts.heading, 'serif')}`, maxWidth: '85%' }}>
        {tagline || 'Your headline, in your own words.'}
      </span>
      <span style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginTop: 'auto' }}>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              height: 34,
              borderRadius: Math.min(radius, 8),
              background: `color-mix(in srgb, ${muted} 22%, ${form.colors.background || '#fff'})`,
            }}
          />
        ))}
      </span>
    </span>
  );
}
